import type { ReactNode } from 'react';

/** `*emphasis*` — a single-asterisk run with no space just inside either marker (so "5 * 3" stays literal). No
 *  lookbehind: it is a SyntaxError on iOS Safari < 16.4, and this module loads on the consent screen. */
const EM = /\*([^*\s](?:[^*\n]*[^*\s])?)\*/g;

function emphasis(text: string, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const m of text.matchAll(EM)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    out.push(<i key={`${keyBase}-i${n++}`}>{m[1]}</i>);
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/**
 * The notice's inline marks: `**bold**` (the deck's `rich` contract — split first, so a bold run is never read as two
 * emphasis markers) and `*emphasis*` inside the unbolded runs (FSD-18 D7: the notice's "*how*" printed its asterisks).
 * Rendering only — the hashed body is never altered.
 */
function inline(text: string): ReactNode {
  const parts = text.split('**');
  if (parts.length < 3) {
    const plain = emphasis(text, 'p');
    return plain.length === 1 ? plain[0] : plain;
  }
  return parts.map((part, i) =>
    i % 2 === 1 ? <b key={`b${i}`}>{part}</b> : <span key={`s${i}`}>{emphasis(part, `s${i}`)}</span>,
  );
}

/**
 * Renders a be-issued notice body (SV-8-3): a deliberately tiny markdown subset — `# title`, `## heading`,
 * `- item`, blank-line paragraphs, `**bold**`, `*emphasis*`. No HTML is ever interpreted: the be already
 * stripped front matter and author comments, and this renderer only ever emits text nodes.
 */
export function NoticeBody({ body, hideTitle = false, testId }: { body: string; hideTitle?: boolean; testId?: string }): JSX.Element {
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  let para: string[] = [];
  let key = 0;
  const flushList = (): void => {
    if (list.length) {
      blocks.push(<ul key={`l${key++}`} className="notice-list">{list.map((li, i) => <li key={i}>{inline(li)}</li>)}</ul>);
      list = [];
    }
  };
  const flushPara = (): void => {
    if (para.length) {
      blocks.push(<p key={`p${key++}`} className="notice-para">{inline(para.join(' '))}</p>);
      para = [];
    }
  };
  for (const raw of body.split(/\r?\n/)) {
    const line = raw.trimEnd();
    if (line.startsWith('# ')) {
      flushList(); flushPara();
      if (!hideTitle) blocks.push(<div key={`t${key++}`} className="h-title sm notice-title">{line.slice(2)}</div>);
    } else if (line.startsWith('## ')) {
      flushList(); flushPara();
      blocks.push(<div key={`h${key++}`} className="pt notice-heading">{line.slice(3)}</div>);
    } else if (line.startsWith('- ')) {
      flushPara();
      list.push(line.slice(2));
    } else if (line.trim() === '') {
      flushList(); flushPara();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushList(); flushPara();
  // VC-12: the notice is the server's text, in the tenant's language — its own first strong letter sets the direction
  return <div className="notice-body" data-testid={testId} dir="auto">{blocks}</div>;
}
